import { Injectable } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "@teammood/shared/ui/dialog";
import { ITeam } from "@teammood/team";
import { Author, TimelineItem, Topic } from "@teammood/timeline";
import { TmCreateTopicDialogComponent } from "./topics/create-topic/create-topic-dialog.component";
import { ModerationDialogComponent } from './moderation/moderation-dialog.component';
import { UserRequestDialogComponent } from "../user-requests/user-request-dialog/user-request-dialog.component";
import { TmCreateCustomKudosDialogComponent } from "./kudos/create-kudos/create-custom-kudos-dialog/create-custom-kudos-dialog.component";
import { UserService } from "@teammood/shared/util/user";
import { LoggerService } from "@teammood/shared/util/logger";
import { catchError, EMPTY, firstValueFrom, from, switchMap } from "rxjs";
import { PollTemplate } from "../../polls/create-poll/create-poll.type";
import { TmSucessDialogComponent } from "../../polls/create-poll/success-dialog/success-dialog.component";
import { TmCreatePollDialogComponent } from "../../polls/create-poll/create-poll-dialog.component";
import { CreateKudosDialogComponent } from "./kudos/create-kudos/create-kudos-dialog/create-kudos-dialog.component";
import { PollPostNowConfirmationDialogComponent } from "../../polls/poll-templates/poll-post-now-confirmation-dialog/poll-post-now-confirmation-dialog.component";

@Injectable(
    { providedIn: 'root' }
)
export class TimelineItemDialogService {

    constructor(
        private logger: LoggerService,
        private dialogService: DialogService,
        private translate: TranslateService,
        private userService: UserService,
    ) {}

    async createKudos(teams: ITeam[], author: Author, nativeKudos: Boolean = false): Promise<MatDialogRef<CreateKudosDialogComponent | TmCreateCustomKudosDialogComponent>> {

        if (nativeKudos) {
            return this.dialogService.open(CreateKudosDialogComponent, {
                width: '100%',
                maxWidth: '1400px',
                enterAnimationDuration: 0,
                exitAnimationDuration: 0,
                data: {
                    teams: teams,
                    author: author
                }
            });
        } else {


            const kudosDialog = from(this.userService.get()).pipe(
                switchMap(user => {
                    const customKudos = user.featureTags.find(feat => feat.id === 'CustomKudosFeature$');

                    if (customKudos && customKudos.values['ctaLabel']) {
                        return this.dialogService.open(TmCreateCustomKudosDialogComponent, {
                            width: '100%',
                            // maxWidth: '520px',
                            maxWidth: '600px',
                            minHeight: '418px',
                            enterAnimationDuration: 0,
                            exitAnimationDuration: 0,
                            data: {
                                teams: teams,
                                customKudos: customKudos.values,
                                author: author
                            }
                        });
                    } else {

                        return this.dialogService.open(CreateKudosDialogComponent, {
                            width: '100%',
                            // maxWidth: '520px',
                            maxWidth: '1400px',
                            enterAnimationDuration: 0,
                            exitAnimationDuration: 0,
                            data: {
                                teams: teams,
                                author: author
                            }
                        });
                    }
                }),
                catchError(err => {
                    this.logger.error(err);
                    return EMPTY;
                })
            )

            return await firstValueFrom(kudosDialog)
        }

    }

    async createQuestion(teamId: string, question: string, templateId: string): Promise<MatDialogRef<TmCreateTopicDialogComponent>> {
        return this.dialogService.open(TmCreateTopicDialogComponent, {
            width: '100%',
            maxWidth: '1400px',
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
            data: {
                teamId: teamId,
                type: 'question',
                question: question,
                templateId: templateId,
            }
        });
    }
    
    async createPoll(teams: ITeam[], templates: PollTemplate[], author: string): Promise<MatDialogRef<TmCreatePollDialogComponent>> {
        return this.dialogService.open(TmCreatePollDialogComponent, {
            minWidth: '380px',
            maxWidth: '1400px',
            width: '100%',
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
            data: {
                teams: teams,
                type: 'AgreeDisagreeQuestion',
                pollTemplates: templates,
                author: author
            }
        });
    }

    async postPollNow(teams: ITeam[], templates: PollTemplate[], author: string): Promise<MatDialogRef<PollPostNowConfirmationDialogComponent>> {
        return this.dialogService.open(PollPostNowConfirmationDialogComponent, {
            minWidth: '380px',
            maxWidth: '800px',
            width: '100%',
            height: 'max-content',
            maxHeight: '90%',
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
            data: {
                teams: teams,
                type: 'AgreeDisagreeQuestion',
                pollTemplates: templates,
                author: author
            }
        });
    }

    async questionSuccess(): Promise<MatDialogRef<TmSucessDialogComponent>> {
        return this.dialogService.open(TmSucessDialogComponent, {
            minWidth: '380px',
            maxWidth: '838px',
            width: '100%',
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
            data: null
        });
    }

    async createIssue(teamId: string): Promise<MatDialogRef<TmCreateTopicDialogComponent>> {
        return this.dialogService.open(TmCreateTopicDialogComponent, {
            width: '100%',
            maxWidth: '1400px',
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
            data: {
                teamId: teamId,
                type: 'issue'
            }
        });
    }

    private notificationDialogContent(topic: Topic, teamSize: number) {
        if (topic.type === 'issue') {
            return '<em>' + this.translate.instant('issue-notification-request-modal.desc2') +'</em>\n' +
                '        <p>\n' +
                '            <span class="notify-highlight">' + teamSize + ' ' + this.translate.instant('issue-notification-request-modal.desc') + '</span>\n' +
                this.translate.instant('issue-notification-request-modal.desc1') +
                '        </p>'
        } else {
            return '<em>' + this.translate.instant('question-notification-request-modal.desc2') + '</em>\n' +
                '        <p>\n' +
                '            <span class="notify-highlight">' + teamSize + ' ' + this.translate.instant('question-notification-request-modal.desc') + '</span>\n' +
                this.translate.instant('question-notification-request-modal.desc1') +
                '        </p>'
        }
    }


    async notify(topic: Topic, teamSize: number) {
        return await this.dialogService.openConfirmDialog({
            icon: '<span><i class="fa-regular fa-bell pe-2"></i></span>',
            title: this.translate.instant('topic-notification-request-modal.title'),
            cancelBtnLabel: this.translate.instant('No'),
            confirmBtnLabel: this.translate.instant('Yes'),
            confirmBtnType: 'primary',
            message: this.notificationDialogContent(topic, teamSize)
        });
    }

    async delete(item: TimelineItem) {
        let itemName: string;

        switch (item.itemType) {
            case 'moodItem':
              itemName = this.translate.instant('item_deletation_confirmation.mood');
              break;
            case 'topicItem':
              itemName = this.translate.instant('item_deletation_confirmation.topic');
              break;
            case 'kudosItem':
              itemName = this.translate.instant('item_deletation_confirmation.kudos');
              break;
        }
        
        return await this.dialogService.openConfirmDialog({
          title: 'item_deletation_confirmation.title',
          message: `${this.translate.instant('item_deletation_confirmation')} ${itemName}`,
          confirmBtnLabel: 'delete',
          confirmBtnType: 'danger'
        });
    }

    async moderate() {
        return await this.dialogService.open(ModerationDialogComponent, {
            width: '100%',
            maxWidth: '1100px',
        });
    }

    async addMoodComment(requestType: string, item: TimelineItem, teamId: string): Promise<MatDialogRef<UserRequestDialogComponent>> {
        return this.dialogService.open(UserRequestDialogComponent, {
            width: '100%',
            maxWidth: '535px',
            height: '490px',
            enterAnimationDuration: 0,
            exitAnimationDuration: 0,
            data: {
                requestType: requestType,
                item: item,
                teamId: teamId
            }
        });
    }
    
}